import React from 'react';
import axios from 'axios';
import {getSportBaseUrl} from '../../../services/sportFeedApi';
import {formattaOra} from '../../../utils/formato';
import {vai, indirizzoEvento} from '../../../utils/vai';
import i18next from 'i18next';

/**
 * CERCA UNA PARTITA dalla testata (modulo Testata › cerca): per nome o per
 * numero evento.
 *
 * COSA FA. Dalla seconda lettera chiede al catalogo
 * (/private/catalog/feed/cerca/{testo}) e apre la tendina con le partite
 * ancora giocabili, dalla piu' vicina: partita, sport · campionato, ora nel
 * fuso di chi guarda, numero evento. Un numero cerca la partita con quel
 * numero. Clic o Invio aprono la pagina dell'evento (/sport/evento/{numero});
 * Esc chiude. Sotto i 1024 px e' una lente: toccata, apre il campo a tutta
 * larghezza sotto la testata e i risultati sotto, alti da dito.
 *
 * COSA NON FA. Non cerca sport, nazioni e campionati: quello lo fa la
 * ricerca del menu sport (che si spegne quando questa e' accesa). Non
 * inventa risultati: quando il catalogo non risponde lo dice, col codice.
 */
export default function CercaPartita({telefono}) {
  const [testo, setTesto] = React.useState('');
  const [risultati, setRisultati] = React.useState(null); // null = niente chiesto
  const [errore, setErrore] = React.useState('');
  const [cercando, setCercando] = React.useState(false);
  const [aperta, setAperta] = React.useState(false);     // la tendina (computer) o il campo (telefono)
  const [scelto, setScelto] = React.useState(0);
  const scatola = React.useRef(null);
  const campo = React.useRef(null);
  const attesa = React.useRef(null);
  const ultima = React.useRef('');

  const chiudi = React.useCallback(() => { setAperta(false); setRisultati(null); setErrore(''); setTesto(''); }, []);

  /* Si chiude cliccando fuori o con Esc, come le altre tendine della barra. */
  React.useEffect(() => {
    if (!aperta) return undefined;
    const fuori = (e) => { if (scatola.current && !scatola.current.contains(e.target)) chiudi(); };
    const tasto = (e) => { if (e.key === 'Escape') chiudi(); };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', tasto);
    return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', tasto); };
  }, [aperta, chiudi]);

  const cerca = (valore) => {
    const pulito = valore.trim();
    ultima.current = pulito;
    window.clearTimeout(attesa.current);
    if (pulito.length < 2) { setRisultati(null); setErrore(''); setCercando(false); return; }
    setCercando(true);
    attesa.current = window.setTimeout(() => {
      axios.get(`${getSportBaseUrl()}/private/catalog/feed/cerca/${encodeURIComponent(pulito)}`)
          .then((res) => {
            if (ultima.current !== pulito) return;
            setRisultati(Array.isArray(res.data) ? res.data : []);
            setErrore('');
            setScelto(0);
            setCercando(false);
          })
          .catch((e) => {
            if (ultima.current !== pulito) return;
            const corpo = e && e.response && e.response.data;
            setErrore((corpo && corpo.code) ? corpo.code + ': ' + (corpo.message || '') : i18next.t('il catalogo non risponde'));
            setRisultati([]);
            setCercando(false);
          });
    }, 300);
  };

  const apri = (partita) => {
    const indirizzo = indirizzoEvento(partita && partita.number);
    if (!indirizzo) return;
    chiudi();
    vai(indirizzo);
  };

  const suTasto = (e) => {
    if (!risultati || !risultati.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setScelto((i) => Math.min(risultati.length - 1, i + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setScelto((i) => Math.max(0, i - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); apri(risultati[scelto]); }
  };

  const nomeDi = (p) => [p.sportName, p.competitionName].filter(Boolean).join(' · ');
  const numero = /^\d+$/.test(testo.trim());

  const campoCerca = (
    <span className={'testata-cerca-campo' + (testo ? ' scrive' : '')}>
      <span className="testata-lente" aria-hidden="true">⌕</span>
      <input ref={campo} type="search" value={testo} placeholder={i18next.t('Cerca partita per nome o numero evento')}
        aria-label={i18next.t('Cerca partita per nome o numero evento')} autoComplete="off"
        onFocus={() => setAperta(true)}
        onChange={(e) => { setTesto(e.target.value); setAperta(true); cerca(e.target.value); }}
        onKeyDown={suTasto}/>
      {testo && <button type="button" className="testata-cerca-x" aria-label={i18next.t('Svuota')} onClick={() => { setTesto(''); setRisultati(null); setErrore(''); campo.current && campo.current.focus(); }}>✕</button>}
    </span>
  );

  const tendina = aperta && (risultati !== null || errore || cercando) && (
    <div className="testata-cerca-tendina" role="listbox" aria-label={i18next.t('Partite trovate')}>
      {cercando && risultati === null && <div className="testata-cerca-riga vuota">{i18next.t('Cerco…')}</div>}
      {errore && <div className="testata-cerca-riga vuota errore">{i18next.t('Non posso cercare:')}{' '}{errore}</div>}
      {!errore && risultati && risultati.length === 0 && !cercando &&
        <div className="testata-cerca-riga vuota">{numero ? i18next.t('Nessuna partita col numero {{v0}}', {v0: testo.trim()}) : i18next.t('Nessuna partita per «{{v0}}»', {v0: testo.trim()})}</div>}
      {!errore && risultati && risultati.map((p, i) => (
        <button type="button" key={p.id || p.number} role="option" aria-selected={i === scelto}
          className={'testata-cerca-riga' + (i === scelto ? ' qui' : '') + (p.playable === false ? ' chiusa' : '')}
          onMouseEnter={() => setScelto(i)} onClick={() => apri(p)}>
          <span className="testata-cerca-num">N. {p.number}</span>
          <b>{p.name}</b>
          <small>{nomeDi(p)}</small>
          <span className="testata-cerca-ora">{formattaOra(p.date, 'giorno-ora')}</span>
        </button>
      ))}
      {!errore && risultati && risultati.length > 0 &&
        <div className="testata-cerca-riga piede">
          <span>{risultati.length === 1 ? i18next.t('1 partita') : risultati.length + ' partite'}{' '}{i18next.t('· dalla più vicina')}{risultati.length >= 20 ? i18next.t(' · scrivi di più per restringere') : ''}</span>
          <span>{telefono ? i18next.t('tocca per aprire la pagina') : i18next.t('Invio apre la scelta · Esc chiude')}</span>
        </div>}
    </div>
  );

  if (telefono) {
    return (
      <span className={'testata-cerca telefono' + (aperta ? ' aperta' : '')} ref={scatola}>
        <button type="button" className={'testata-lente-tasto' + (aperta ? ' qui' : '')} aria-label={i18next.t('Cerca partita')}
          aria-expanded={aperta}
          onClick={() => { if (aperta) chiudi(); else { setAperta(true); window.setTimeout(() => campo.current && campo.current.focus(), 0); } }}>⌕</button>
        {aperta &&
          <div className="testata-cerca-foglio">
            <div className="testata-cerca-foglio-riga">
              {campoCerca}
              <button type="button" className="testata-cerca-chiudi" onClick={chiudi}>{i18next.t('Chiudi')}</button>
            </div>
            {tendina}
          </div>}
      </span>
    );
  }

  return (
    <span className={'testata-cerca' + (aperta ? ' aperta' : '')} ref={scatola}>
      {campoCerca}
      {tendina}
    </span>
  );
}
